import { Fragment, PureComponent } from 'react';
import PropTypes from 'prop-types';
import { connect } from 'react-redux';
import { Route } from 'react-router-dom';
import { withRouter, matchPath } from 'react-router';
import 'focus-visible';

import JLUiKitStaticContent from 'jl-design-system/utils/content/staticContent';
import { ChevronLeftOutlined } from 'jl-design-system/components/icons/ChevronLeftOutlined';
import ESIInclude from 'jl-esi-components';
import { Header, BPHeader } from 'jl-my-account/components';
import FooterBanner from '../../../browse-components/email-signup/FooterBanner';

import { ErrorPage, errorPageActions } from '../error-page';
import { errorTypes, errorMessagesActions } from '../messages';
import { Grid } from '../grid';

import { Headline } from './headline';

import styles from './Layout.scss';

import { staticContent as defaultStaticContent } from './static-content.constants';
import { getDisplayName, isString } from '~/utils';
import { LoadingSpinner, envConfigSelectors } from '~/components';
import { Button } from '~/elements';
import { containsMessage } from './helpers';
import { getAccessToken } from '~/hocs/utils';
import { FeaturesContext } from '~/components/features/with-features.hoc';

JLUiKitStaticContent.set(defaultStaticContent);

const content = defaultStaticContent;
const basketCount = null;
const defaultHeaderHeight = 103;

const pathsWithoutHangOn = ['/fitting-type', '/shops', '/completed-booking', '/select-treatment'];

export const withLayout = (Content) =>
  class extends PureComponent {
    static contextType = FeaturesContext;

    static propTypes = {
      containerClassName: PropTypes.string,
      location: PropTypes.shape({
        pathname: PropTypes.string.isRequired,
        search: PropTypes.string,
      }).isRequired,

      resetTryAgain: PropTypes.bool.isRequired,

      displaySpinner: PropTypes.bool.isRequired,
      errorPageStatus: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
      config: PropTypes.shape({
        breadcrumb: PropTypes.shape({
          to: PropTypes.string.isRequired,
        }),
        headlineTitle: PropTypes.oneOfType([PropTypes.string, PropTypes.func]).isRequired,
      }).isRequired,
      headlineBackButtonConfig: PropTypes.shape({
        title: PropTypes.string,
        isDisplayed: PropTypes.bool,
        goBack: PropTypes.func,
      }).isRequired,
      digiMerchHeaderFeature: PropTypes.shape({
        enabled: PropTypes.bool,
      }).isRequired,
      dispatchTryAgainClicked: PropTypes.func.isRequired,
      dispatchClearErrorStatus: PropTypes.func.isRequired,
    };

    static defaultProps = {
      errorPageStatus: null,
      containerClassName: '',
    };

    state = {
      headerHeight: defaultHeaderHeight,
      accessToken: undefined,
    };

    static displayName = `WithLayout(${getDisplayName(Content)})`;

    async componentDidMount() {
      this.setState({
        accessToken: await getAccessToken(),
      });
      window.addEventListener('scroll', this.setSpinnerOverlayPosition);
    }

    componentDidUpdate() {
      const {
        errorPageStatus,
        location: { pathname },

        dispatchClearErrorStatus,
      } = this.props;

      const isPathWithHangOn = !pathsWithoutHangOn.some((path) => matchPath(pathname, { path }));
      if (errorPageStatus && !isPathWithHangOn && pathname !== '/shops') {
        dispatchClearErrorStatus();
      }
    }

    componentWillUnmount() {
      window.removeEventListener('scroll', this.setSpinnerOverlayPosition);
    }

    setSpinnerOverlayPosition = () => {
      this.setState(() => ({
        headerHeight: Math.max(0, defaultHeaderHeight - window.pageYOffset),
      }));
    };

    resetTryAgainClicked = (event) => {
      const { dispatchTryAgainClicked, resetTryAgain } = this.props;

      if (resetTryAgain && event.target.id !== 'loading-spinner') {
        dispatchTryAgainClicked(false);
      }
    };

    render() {
      const {
        displaySpinner,
        errorPageStatus,
        containerClassName,
        config: { breadcrumb, headlineTitle },
        headlineBackButtonConfig,
        location,
        digiMerchHeaderFeature,
      } = this.props;

      const { headerHeight, accessToken } = this.state;

      const { isBPLayoutActive } = this.context;

      return (
        <Fragment>
          {digiMerchHeaderFeature.enabled ? (
            <div id="header-wrapper">
              <ESIInclude
                id="esi-header"
                src="/scaffold/header.html?includeCoreCSS=false&includeCoreJS=false&includePolyfills=false"
              />
            </div>
          ) : isBPLayoutActive ? (
            <BPHeader
              userInfo={accessToken}
              content={content}
              basketCount={basketCount}
              breadcrumbConfig={location.hash === '#landing' ? undefined : breadcrumb}
            />
          ) : (
            <Header
              userInfo={accessToken}
              content={content}
              basketCount={basketCount}
              breadcrumbConfig={location.hash === '#landing' ? undefined : breadcrumb}
            />
          )}
          {errorPageStatus ? (
            <ErrorPage errorType={errorPageStatus} />
          ) : (
            <main
              id="main"
              className={styles.PersonalStyling}
              role="main"
              onClick={this.resetTryAgainClicked}
            >
              <Headline title={isString(headlineTitle) ? headlineTitle : ''}>
                {headlineBackButtonConfig.isDisplayed && (
                  <Button
                    data-testid="back-button"
                    type="button"
                    className={styles.BackButton}
                    onClick={headlineBackButtonConfig.goBack}
                  >
                    <ChevronLeftOutlined size="medium" />
                    <span className={styles.Text}>{headlineBackButtonConfig.title}</span>
                  </Button>
                )}
              </Headline>
              <Grid.Container className={containerClassName}>
                <Content {...this.props} />
              </Grid.Container>
              {displaySpinner && (
                <LoadingSpinner
                  centered
                  loadingSpinnerStyle={{ position: 'fixed', top: headerHeight }}
                />
              )}
            </main>
          )}
          <Route
            path="/bookings/:bookingId"
            render={() => (
              <FooterBanner
                title="Be in the know"
                message="Get inspiration, new arrivals and the latest offers to your inbox"
                action="Sign me up for emails"
              />
            )}
          />
        </Fragment>
      );
    }
  };

export const mapStateToProps = (state) => {
  const {
    messages: {
      app: { errorMessages, tryAgainClicked },
    },
    loader: { isCallFinished, numberOfCallsInProgress },
    errorPage: { status },
    services: { config, routeConfig },
  } = state;

  return {
    resetTryAgain:
      tryAgainClicked && !containsMessage(errorMessages, errorTypes.STILL_CANT_LOAD_THE_PAGE_ERROR),
    displaySpinner: !isCallFinished || !!numberOfCallsInProgress,
    errorPageStatus: status,
    config,
    headlineBackButtonConfig: routeConfig.headlineBackButton,
    digiMerchHeaderFeature: envConfigSelectors.digiMerchHeaderFeature(state),
  };
};

const mapDispatchToProps = {
  dispatchTryAgainClicked: errorMessagesActions.tryAgainClicked,
  dispatchClearErrorStatus: errorPageActions.clearErrorStatus,
};

const ConnectedWithLayout = (Component) =>
  connect(mapStateToProps, mapDispatchToProps)(withLayout(Component));

export const ConnectedWithLayoutRouter = (Component) => withRouter(ConnectedWithLayout(Component));
